import React, { useEffect, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import { Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/lib/table/interface";

import { Page } from "../../components/page";
import { PageHeader } from "../../components/page_header";
import { DateRange, MarketSelector, Selector } from "../../components/selectors";
import { feedbacksChartFetchRequest, feedbacksFetchRequest } from "../../store/feedbacks/actions";
import { FeedbackItem } from "../../store/feedbacks/types";
import { RootState } from "../../store/root_reducer";
import { enumParams } from "../../utils/enum_params";
import { MomentParam } from "../../utils/moment_param";

import { DataTable, FeedbacksChart, FeedbacksChartType } from "./components";
import styles from "./styles.module.scss";

const chartTypeOptions: FeedbacksChartType[] = ["ratings", "csi"];

export const FeedbacksPage: React.FC = () => {
  const { t } = useTranslation("feedbacks_page");
  const feedbacksState = useSelector((state: RootState) => state.Feedbacks);
  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);
  const markets = useSelector((state: RootState) => state.Markets.markets);

  const [refresh, setRefresh] = useState(true);
  const [commonState, setCommonState] = useQueryParams({
    from: withDefault(MomentParam, moment.utc().startOf("day").subtract(1, "week").add(1, "day")),
    to: withDefault(MomentParam, moment.utc().startOf("day")),
    market: withDefault(StringParam, undefined),
  });
  const [chartState, setChartState] = useQueryParams({
    chart: withDefault(enumParams<FeedbacksChartType>(StringParam, chartTypeOptions), "ratings"),
  });
  const [tableState, setTableState] = useQueryParams({
    page: withDefault(NumberParam, 1),
    pageSize: withDefault(NumberParam, 10),
  });

  const dispatch = useDispatch();
  useEffect(() => {
    setCommonState((state) => state, "replaceIn");
    setChartState((state) => state, "replaceIn");
    setTableState((state) => state, "replaceIn");
  }, [setCommonState, setChartState, setTableState]);

  useEffect(() => {
    dispatch(feedbacksFetchRequest({ ...commonState, ...tableState, gateId: selectedGate.id }));
  }, [dispatch, commonState, tableState, selectedGate, refresh]);

  useEffect(() => {
    dispatch(feedbacksChartFetchRequest({ ...commonState, gateId: selectedGate.id }));
  }, [dispatch, commonState, selectedGate, refresh]);

  const handleDateRangeSelect = (value: DateRange) => {
    setCommonState((state) => ({ ...state, ...value }));
  };
  const handleMarketSelect = (market?: string) => {
    setCommonState((state) => ({ ...state, market }));
    setTableState((state) => ({ ...state, page: 1 }));
  };
  const handleChartTypeSelect = (value?: string) => {
    setChartState((state) => ({ ...state, chart: value as FeedbacksChartType }));
  };

  const handleTableChange = (
    { current, pageSize }: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<FeedbackItem> | SorterResult<FeedbackItem>[],
    { action }: TableCurrentDataSource<FeedbackItem>
  ) => {
    switch (action) {
      case "paginate": {
        if (current && pageSize) {
          setTableState((state) => ({ ...state, page: state.pageSize !== pageSize ? 1 : current, pageSize }));
        }
        break;
      }
      default:
        break;
    }
  };

  const isLoading = feedbacksState.dataIsLoading || feedbacksState.chartDataIsLoading;

  return (
    <Page title={t("title")} description={t("description")} reportTTL={365}>
      <PageHeader
        onReload={() => setRefresh((state) => !state)}
        isLoading={isLoading}
        onDateRangeSelect={handleDateRangeSelect}
        dateRange={commonState}
      >
        <MarketSelector markets={markets} value={commonState.market} onSelect={handleMarketSelect} />
        <Selector
          value={chartState.chart}
          className={styles.chartTypeSelector}
          title={t("selectors.chartType.title")}
          placeholder={t("selectors.chartType.placeholder")}
          onSelect={handleChartTypeSelect}
          options={chartTypeOptions.map((option) => ({
            value: option,
            title: t(`selectors.chartType.options.${option}`),
          }))}
        />
      </PageHeader>
      <FeedbacksChart type={chartState.chart} />
      <DataTable
        loading={feedbacksState.dataIsLoading}
        dataSource={feedbacksState.items}
        currentPage={tableState.page}
        pageSize={tableState.pageSize}
        totalFeedbacks={feedbacksState.count}
        onChange={handleTableChange}
      />
    </Page>
  );
};
